// Data sources (/datasource) — the reference files forms look values up from.
// Header (title, subtitle, Import, Add new) over a toolbar (debounced search,
// returned count, Columns menu) and the common DataTable + TableFooter, all
// server-side via POST datasource/all (search, single-column sort, paging).
// A row opens that source's entries.
//
// The selection bar and the row kebab carry the same three actions, but they
// split by cardinality: Edit and View file entries target ONE record so they
// hide once 2+ rows are picked, while Delete acts on the whole selection. Delete
// is a true bulk endpoint, so the kebab's single delete and the bar's multi
// delete converge on one call and one confirmation.
//
// Edit reuses the create modal (PATCH takes the create body unchanged), seeded
// from the row itself — a list row already carries all three editable fields, so
// opening the editor costs no extra request.
//
// Import is a real menu now, not a lone button. Upload CSV opens a hidden file
// input, and the picked file is parsed and validated BEFORE any modal appears —
// a bad CSV is refused with a toast and nothing opens. Download example template
// builds the sample in the browser (see dataSourceTemplate) and saves it; it
// needs no endpoint.

import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ColumnsMenu,
  ConfirmModal,
  DataTable,
  Dropdown,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import useModuleAccess, { useCanImportDataSources } from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  DataSourceSortKey,
  useDeleteDataSourcesMutation,
  useGetDataSourcesQuery,
  type DataSourceListItem,
} from '@/api/endpoints/datasourceApi';
import { FormDateFilter } from '@/api/endpoints/formsApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import {
  DATE_ANY,
  buildDateFilterOptions,
  dateFilterOf,
  dateRangeOf,
} from '@/utils/dateFilterPresets';
import { useDataSourceColumns } from './sources/dataSourceColumns';
import DataSourceModal, { type EditableDataSource } from './sources/DataSourceModal';
import DataSourceImportModal from './sources/DataSourceImportModal';
import { parseDataSourceCsv, type ParsedDataSourceCsv } from './shared/parseDataSourceCsv';
import { TEMPLATE_FILE_NAME, buildDataSourceTemplateCsv } from './shared/dataSourceTemplate';
import { downloadTextFile } from '@/utils/downloadUrl';
import { DEV_ONLY } from '@/config/config';

type SortType = 'asc' | 'desc';

/** Just the row facts the selection bar's actions need — not the whole row.
 *  Keeping them per selected id means "View file entries" still knows the
 *  source's name, and Edit still has its seed, for a row selected on an earlier
 *  page. They are exactly the modal's editable fields, hence the alias. */
type SelectedSource = EditableDataSource;
const toSelected = (ds: DataSourceListItem): SelectedSource => ({
  id: ds.id,
  schemaName: ds.schemaName,
  dataSourceType: ds.dataSourceType,
  externalId: ds.externalId,
});

/** One pending delete, whichever path opened it. `name` is set only when a
 *  single row was targeted, so the confirmation can say which. */
interface PendingDelete {
  ids: string[];
  name?: string;
}

// Toolbar Filter category keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['lastUpdatedBy', 'lastUpdated'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

const DataSourcePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Verb gates (hidden, not disabled, when missing). Import is the module's
  // own extra — it does NOT ride create — and covers the whole Import menu,
  // template download included (useless without the right to import). The
  // read paths (row click, View file entries) stay ungated: reaching this
  // page already implies `view`.
  const { canCreate, canEdit, canDelete } = useModuleAccess('data_sources');
  const canImport = useCanImportDataSources();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // The sortable columns are keyed by DataSourceSortKey itself, so this state IS
  // the request's orderBy — there is no UI-key → backend-key map to keep in sync.
  // EXTERNAL ID desc, matching the design's active header.
  const [sort, setSort] = useState<{ key: DataSourceSortKey; type: SortType }>({
    key: DataSourceSortKey.ExternalId,
    type: 'desc',
  });
  // One state for both modal modes: null = closed, `{}` = add new, and a
  // `dataSource` = edit that one (the entries page's entryModal idiom).
  const [sourceModal, setSourceModal] = useState<{ dataSource?: SelectedSource } | null>(null);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  // Set only once a CSV has parsed AND validated, so the modal never has to
  // render a failure state.
  const [importTarget, setImportTarget] = useState<{
    file: File;
    parsed: ParsedDataSourceCsv;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<Map<string, SelectedSource>>(new Map());
  const [confirmDelete, setConfirmDelete] = useState<PendingDelete | null>(null);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom-range FROM/TO for the Last updated category.
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});

  // Last-updated-by options — fetched up-front so the pane is ready on first open.
  const { data: companyUsers = [] } = useGetUsersSelectionQuery();

  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'lastUpdatedBy',
        label: t('datasource_col_last_updated_by'),
        icon: 'user-pen',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: companyUsers.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'lastUpdated',
        label: t('datasource_col_last_updated'),
        icon: 'history',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: buildDateFilterOptions(t),
      },
    ],
    [t, companyUsers],
  );

  const lastUpdated = dateFilterOf(filters, 'lastUpdated');

  const { data, currentData, isLoading, isFetching, isError } = useGetDataSourcesQuery(
    {
      search: debouncedSearch || undefined,
      orderBy: sort,
      page,
      limit,
      lastUpdatedBy: filters.lastUpdatedBy.size ? [...filters.lastUpdatedBy] : undefined,
      lastUpdated,
      lastUpdatedStart: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'start'),
      lastUpdatedEnd: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'end'),
    },
    { refetchOnMountOrArgChange: true },
  );

  // A new search, filter or sort narrows or reorders the set — snap back to
  // page 1 so the pager can't sit past the end of the new list.
  useEffect(() => setPage(1), [debouncedSearch, filters, dateRanges, sort]);

  const [deleteDataSources, { isLoading: deleting }] = useDeleteDataSourcesMutation();

  // Parse and validate before anything opens: the import modal treats its file
  // as already-good, so a bad CSV must be refused here.
  const onPickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clearing the value is what lets the SAME file be picked twice — without
    // it the input's value never changes and onChange never fires again.
    e.target.value = '';
    if (!file) return;
    const result = await parseDataSourceCsv(file);
    if (result.status === 'ok') {
      setImportTarget({ file, parsed: result.parsed });
      return;
    }
    // One line for the user, naming the first problem so they know where to
    // look; the whole list goes to the console for whoever is debugging a file
    // with dozens of them (the legacy importer's split, and api/client.ts's
    // DEV_ONLY logging convention).
    if (DEV_ONLY && result.cellErrors) console.log('CSV import errors:', result.cellErrors);
    toast.error(t(result.errorKey, result.params));
  };
  const openEntries = (ds: SelectedSource) =>
    navigate('/datasource/entries', {
      state: { dataSourceId: ds.id, dataSourceName: ds.schemaName },
    });

  const columns = useDataSourceColumns({
    onEdit: canEdit ? (ds) => setSourceModal({ dataSource: toSelected(ds) }) : undefined,
    onViewEntries: openEntries,
    onDelete: canDelete
      ? (ds) => setConfirmDelete({ ids: [ds.id], name: ds.schemaName })
      : undefined,
  });
  const { visibleColumns, menuProps } = useColumnVisibility(columns, {
    locked: [DataSourceSortKey.SchemaName],
    excluded: ['actions'],
  });

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // DataTable wants a Set of ids; the Map keeps each row's facts alongside, so
  // the bar's actions work for rows selected on an earlier page.
  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((d) => d.id === id);
        if (row) next.set(id, toSelected(row));
      }
      return next;
    });

  // Header select-all — scoped to the current page's rows, as the other lists do.
  const allSelected = items.length > 0 && items.every((d) => selected.has(d.id));
  const someSelected = !allSelected && items.some((d) => selected.has(d.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((d) => next.delete(d.id));
      else items.forEach((d) => next.set(d.id, toSelected(d)));
      return next;
    });

  const runDelete = async () => {
    if (!confirmDelete) return;
    const { ids, name } = confirmDelete;
    try {
      await deleteDataSources({ dataSourceIds: ids }).unwrap();
      toast.success(
        name
          ? t('datasource_deleted', { name })
          : t('datasource_bulk_deleted', { count: ids.length }),
      );
      // Drop the deleted rows from the selection — they no longer exist.
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setConfirmDelete(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Same key toggles direction; a new key resets to desc. DataTable types onSort
  // as (key: string), but only sortable columns can fire it and each is keyed by
  // a DataSourceSortKey — hence the cast at the boundary.
  const handleSort = (raw: string) => {
    const key = raw as DataSourceSortKey;
    setSort((prev) =>
      prev.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'desc' },
    );
  };

  return (
    <div className='datasource-page'>
      <header className='datasource-page__header'>
        <div>
          <h1 className='datasource-page__title'>{t('datasource_title')}</h1>
          <p className='datasource-page__subtitle'>{t('datasource_subtitle')}</p>
        </div>
        <div className='datasource-page__header-actions'>
          {/* The chevron finally means something: this is a real menu. The
              whole menu rides the `import` extra — gating only the items would
              leave a button opening an empty panel. */}
          {canImport && (
            <Dropdown
              open={importMenuOpen}
              onClose={() => setImportMenuOpen(false)}
              align='end'
              width={240}
              trigger={
                <Button
                  variant='secondary'
                  iconStart='upload'
                  iconEnd='chevron-down'
                  onClick={() => setImportMenuOpen((o) => !o)}
                >
                  {t('datasource_action_import')}
                </Button>
              }
            >
              <button
                type='button'
                role='menuitem'
                className='app-dropdown__item'
                onClick={() => {
                  setImportMenuOpen(false);
                  fileInputRef.current?.click();
                }}
              >
                <Icon name='file-spreadsheet' size={16} />
                <span className='app-dropdown__item-label'>
                  {t('datasource_import_upload_csv')}
                </span>
              </button>
              {/* Generated in the browser, not fetched — the example is static, so
                a network round trip would only add a way for it to fail. */}
              <button
                type='button'
                role='menuitem'
                className='app-dropdown__item'
                onClick={() => {
                  setImportMenuOpen(false);
                  downloadTextFile(buildDataSourceTemplateCsv(), TEMPLATE_FILE_NAME);
                }}
              >
                <Icon name='file-down' size={16} />
                <span className='app-dropdown__item-label'>
                  {t('datasource_import_download_template')}
                </span>
              </button>
            </Dropdown>
          )}
          {canCreate && (
            <Button iconStart='plus' onClick={() => setSourceModal({})}>
              {t('datasource_new')}
            </Button>
          )}
        </div>
      </header>

      <div className='datasource-page__toolbar'>
        <div className='datasource-page__toolbar-lead'>
          <div className='datasource-page__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('datasource_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='datasource-page__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('datasource_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            ranges={dateRanges}
            onApply={(next, ranges) => {
              setFilters(next);
              setDateRanges(ranges);
            }}
            emptyText={t('text_no_results_found')}
          />
        </div>
        <div className='datasource-page__toolbar-trail'>
          <span className='datasource-page__count'>{t('datasource_count', { count: total })}</span>
          <ColumnsMenu {...menuProps} />
        </div>
      </div>

      <Card padded={false} className='datasource-page__panel'>
        {selected.size > 0 && (
          <div className='datasource-page__selection-bar'>
            <div className='datasource-page__selection-lead'>
              <span className='datasource-page__selected'>
                {t('datasource_selected_count', { count: selected.size })}
              </span>
              {/* The divider needs something after it: single-selection always
                  has View file entries, multi-selection only Delete. */}
              {(onlySelected || canDelete) && (
                <span className='datasource-page__selection-divider' />
              )}
              {/* Edit and View file entries name ONE source, so they drop out
                  once a second row is picked. Delete acts on the whole
                  selection. */}
              {onlySelected && (
                <>
                  {canEdit && (
                    <Button
                      variant='ghost'
                      iconStart='square-pen'
                      onClick={() => setSourceModal({ dataSource: onlySelected })}
                    >
                      {t('datasource_action_edit')}
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    iconStart='table-2'
                    onClick={() => openEntries(onlySelected)}
                  >
                    {t('datasource_action_view_entries')}
                  </Button>
                </>
              )}
              {canDelete && (
                <Button
                  variant='ghost'
                  iconStart='trash-2'
                  className='datasource-page__selection-delete'
                  onClick={() => setConfirmDelete({ ids: [...selected.keys()] })}
                >
                  {t('button_delete')}
                </Button>
              )}
            </div>
            <Button variant='ghost' onClick={() => setSelected(new Map())}>
              {t('button_clear')}
            </Button>
          </div>
        )}

        <DataTable
          columns={visibleColumns}
          rows={items}
          rowKey={(d) => d.id}
          selectable
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('datasource_select_all')}
          sort={sort}
          onSort={handleSort}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={1100}
          onRowClick={(row) =>
            navigate('/datasource/entries', {
              state: { dataSourceId: row.id, dataSourceName: row.schemaName },
            })
          }
          empty={
            <>
              <Icon name='database' size={26} />
              {t('datasource_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('datasource_items_per_page')}
          rangeText={t('datasource_range', { from, to, total })}
        />
      </Card>

      {/* Lives outside the menu so dismissing the dropdown can't unmount the
          input mid-pick. Hidden rather than styled — the menu item is the
          affordance. */}
      <input ref={fileInputRef} type='file' accept='.csv,text/csv' hidden onChange={onPickFile} />

      {/* Mounted only once a file has parsed AND validated, so `file`/`parsed`
          are never optional inside it. */}
      {importTarget && (
        <DataSourceImportModal
          open
          onClose={() => setImportTarget(null)}
          file={importTarget.file}
          parsed={importTarget.parsed}
        />
      )}

      {/* Keyed on the target so switching straight from one row's Edit to
          another's re-seeds the draft — the modal only re-seeds while `open`,
          which never flips in that case. */}
      <DataSourceModal
        key={`source-${sourceModal?.dataSource?.id ?? 'new'}`}
        open={!!sourceModal}
        onClose={() => setSourceModal(null)}
        dataSource={sourceModal?.dataSource}
      />

      {/* One modal for both paths — the endpoint is bulk, so a single row and a
          whole selection are the same call; only the title differs. */}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={
            confirmDelete.name
              ? t('datasource_delete_confirm_title', { name: confirmDelete.name })
              : t('datasource_bulk_delete_title', { count: confirmDelete.ids.length })
          }
          description={t('datasource_delete_confirm_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={deleting}
        />
      )}
    </div>
  );
};

export default DataSourcePage;
